import React, { useEffect, useRef, useState } from "react";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist/build/pdf";

GlobalWorkerOptions.workerSrc =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

const isImageFile = (path) => /\.(png|jpe?g|gif|webp)$/i.test(path || "");

export const ArtworkViewer = ({ filePath }) => {
  const stageRef = useRef(null);
  const [zoom, setZoom] = useState(100);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const url = filePath ? `https://api.bebranded.com.au/${filePath}` : "";
  const image = isImageFile(filePath);

  useEffect(() => {
    setZoom(100);
  }, [filePath]);

  useEffect(() => {
    if (!url || image) {
      setPages([]);
      setError("");
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const renderPdf = async () => {
      setLoading(true);
      setError("");
      setPages([]);
      try {
        const pdf = await getDocument({ url, disableRange: true }).promise;
        const stageWidth = Math.max((stageRef.current?.clientWidth || 720) - 24, 280);
        const rendered = [];
        for (let i = 1; i <= pdf.numPages; i += 1) {
          const page = await pdf.getPage(i);
          const base = page.getViewport({ scale: 1 });
          const fit = stageWidth / base.width;
          const viewport = page.getViewport({ scale: fit * (zoom / 100) });
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          await page.render({ canvasContext: context, viewport }).promise;
          if (cancelled) return;
          rendered.push(canvas.toDataURL("image/jpeg", 0.92));
        }
        if (!cancelled) setPages(rendered);
      } catch (err) {
        console.error("Artwork preview failed:", err);
        if (!cancelled) setError("This file could not be shown. Try again in a moment.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    renderPdf();
    return () => {
      cancelled = true;
    };
  }, [url, image, zoom]);

  if (!filePath) return null;

  return (
    <div className="proof-viewer">
      <div className="proof-toolbar">
        <button type="button" onClick={() => setZoom((z) => Math.max(50, z - 25))} aria-label="Zoom out">
          −
        </button>
        <span>{zoom}%</span>
        <button type="button" onClick={() => setZoom((z) => Math.min(250, z + 25))} aria-label="Zoom in">
          +
        </button>
        <button type="button" className="proof-fit" onClick={() => setZoom(100)}>
          Fit width
        </button>
      </div>
      <div className="proof-stage" ref={stageRef}>
        {image && (
          <img
            alt="Artwork"
            src={url}
            style={{ width: `${zoom}%`, maxWidth: "none", display: "block", margin: "0 auto" }}
          />
        )}
        {!image && loading && <p className="proof-note">Loading artwork…</p>}
        {!image && error && <p className="proof-note">{error}</p>}
        {!image &&
          pages.map((src, i) => (
            <img key={`${filePath}-${i}-${zoom}`} alt={`Page ${i + 1}`} src={src} className="proof-page" />
          ))}
      </div>
    </div>
  );
};
